/**
 * 🔍 Utilitaires de recherche RNCP
 * Fonctions partagées entre RncpTest.jsx et IntercariforefTest.jsx
 *
 * Ce fichier centralise toute la logique de:
 * - Extraction de préfixes de diplômes (BTS, CAP, DE, CS, etc.)
 * - Calcul de score de pertinence
 * - Normalisation de chaînes
 * - Détection d'options (A, B, C)
 */

/**
 * Normaliser une chaîne pour la comparaison
 * @param {string} str - Chaîne à normaliser
 * @returns {string} Chaîne normalisée (minuscules, sans accents, sans ponctuation)
 */
export const normalizeStr = (str) => {
  if (!str) return '';
  return str.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Liste des préfixes de diplômes reconnus (triés du plus long au plus court)
 */
export const DEGREE_PREFIXES = [
  'CERTIFICAT DE SPECIALISATION', 'DIPLOME D\'ETAT', 'DIPLOME D ETAT', 'TITRE PROFESSIONNEL',
  'BAC PRO', 'DOCTORAT', 'LICENCE', 'MASTER', 'DEUST', 'DNSEP',
  'TP', 'BAC', 'BTS', 'DUT', 'CAP', 'BEP', 'MC', 'BP', 'BTM', 'BUT',
  'DN', 'DNA', 'DE', 'DEME', 'DEES', 'CQP', 'CPGE', 'DCG', 'DSCG', 'CSA', 'CS'
];

/**
 * Liste des préfixes pour le scoring (forme normalisée)
 */
export const ALL_PREFIXES_FOR_SCORING = [
  'certificat de specialisation', 'diplome d etat', 'diplome d\'etat', 'titre professionnel',
  'bac pro', 'bts', 'cap', 'bac', 'master', 'licence',
  'tp', 'dut', 'but', 'bep', 'mc', 'doctorat', 'deust', 'de', 'cs', 'bp', 'btm'
];

/**
 * Extraire le préfixe de diplôme d'une requête
 * Ex: "BTS - Cybersécurité" → { prefix: "BTS", coreQuery: "Cybersécurité", originalQuery: "BTS - Cybersécurité" }
 *
 * @param {string} query - Requête de recherche
 * @returns {Object} { prefix: string|null, coreQuery: string, originalQuery: string }
 */
export const extractDegreePrefix = (query) => {
  // Normaliser pour la comparaison
  const normalizeForMatching = (str) => {
    return str.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
      .replace(/['']/g, ' ')          // Remplacer apostrophes
      .toUpperCase()
      .replace(/\s+/g, ' ')
      .trim();
  };

  const normalizedQuery = normalizeForMatching(query);

  // Tester chaque préfixe du plus long au plus court
  for (const prefix of DEGREE_PREFIXES) {
    const normalizedPrefix = normalizeForMatching(prefix);
    const escapedPrefix = normalizedPrefix.replace(/\s+/g, '\\s+');

    // Regex : préfixe + (tiret avec espaces OU juste espaces) + reste
    const regex = new RegExp(`^${escapedPrefix}(?:\\s*-\\s*|\\s+)(.+)$`, 'i');
    const match = normalizedQuery.match(regex);

    if (match) {
      // ✅ Créer une regex pour supprimer le préfixe de la requête originale
      // Échapper les caractères spéciaux du préfixe
      const prefixForRegex = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const prefixRegex = new RegExp(`^${prefixForRegex}(?:\\s*-\\s*|\\s+)`, 'i');
      const coreQuery = query.replace(prefixRegex, '').trim();

      return {
        prefix: prefix,
        coreQuery: coreQuery,
        originalQuery: query
      };
    }
  }

  return {
    prefix: null,
    coreQuery: query,
    originalQuery: query
  };
};

/**
 * Calculer le score de pertinence entre une requête et un titre de certification
 *
 * @param {string} query - Requête de recherche originale
 * @param {string} title - Titre de la certification
 * @param {string} rncpCode - Code RNCP de la certification (optionnel)
 * @param {boolean} isActive - La certification est-elle active? (default: true)
 * @returns {number} Score de 0 à 100
 */
export const calculateScore = (query, title, rncpCode = '', isActive = true) => {
  const extractWords = (str) => {
    const stopWords = ['le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'ou', 'en', 'a', 'au', 'aux', 'pour', 'par', 'dans', 'sur', 'avec'];
    return normalizeStr(str).split(' ').filter(w => w.length > 2 && !stopWords.includes(w));
  };

  // Extraire le préfixe de diplôme
  const { prefix, coreQuery } = extractDegreePrefix(query);
  const searchQuery = prefix ? coreQuery : query;

  const normalizedQuery = normalizeStr(searchQuery);
  const normalizedTitle = normalizeStr(title);
  const queryWords = extractWords(searchQuery);
  const titleWords = extractWords(title);

  let score = 0;

  // 1. Correspondance exacte
  if (normalizedQuery === normalizedTitle) return 100;

  // 2. Correspondance code RNCP
  if (rncpCode && normalizedQuery.replace(/\s+/g, '') === rncpCode.toLowerCase()) {
    return 95;
  }

  // 3. Titre contient exactement la requête (avec espaces)
  if (normalizedTitle.includes(normalizedQuery)) {
    score = 80;
  }
  // 4. Titre contient la requête complète
  else if (normalizedTitle.includes(normalizedQuery)) {
    score = 60;
  }

  // 5. Analyse avec pondération
  const genericWords = ['bac', 'pro', 'master', 'bts', 'cap', 'diplome', 'titre', 'professionnel', 'conduite', 'production', 'productions', 'gestion', 'technique', 'techniques', 'responsable', 'charge', 'chef', 'agent', 'arts', 'metiers', 'metier', 'service', 'services', 'certificat', 'specialisation', 'manager', 'unite', 'unites'];
  const commonWords = [];
  const missingWords = [];

  queryWords.forEach(qw => {
    const weight = genericWords.includes(qw) ? 0.3 : 1.0;
    const found = titleWords.some(tw => tw.includes(qw) || qw.includes(tw));

    if (found) {
      commonWords.push({ word: qw, weight });
    } else {
      missingWords.push({ word: qw, weight });
    }
  });

  const totalWeight = queryWords.reduce((sum, w) =>
    sum + (genericWords.includes(w) ? 0.3 : 1.0), 0
  );
  const matchedWeight = commonWords.reduce((sum, item) => sum + item.weight, 0);
  const missingWeight = missingWords.reduce((sum, item) => sum + item.weight, 0);
  const ratio = totalWeight > 0 ? matchedWeight / totalWeight : 0;

  // Règle stricte : < 50% de correspondance
  if (ratio < 0.5) {
    score = Math.max(score, ratio * 30);
    if (missingWeight > 0.5) {
      score *= 0.5;
    }
    return Math.min(100, Math.max(0, score));
  }

  // Score basé sur le ratio
  const wordScore = ratio * 60;
  score = Math.max(score, wordScore);

  // Bonus si tous les mots trouvés
  if (commonWords.length === queryWords.length) {
    score += 20;
  }

  // Bonus ordre des mots
  let orderScore = 0;
  let lastIndex = -1;
  const normalizedTitleStr = normalizeStr(title);

  for (let qw of queryWords) {
    const idx = normalizedTitleStr.indexOf(qw, lastIndex + 1);
    if (idx > lastIndex) {
      orderScore += 2;
      lastIndex = idx;
    }
  }
  score += orderScore;

  // Pénalité pour mots manquants
  if (missingWords.length > 0) {
    const penalty = missingWords.reduce((sum, w) => sum + w.weight * 5, 0);
    score -= penalty;
  }

  // Bonus acronymes
  const queryAcronyms = searchQuery.match(/\b[A-Z]{2,}\b/g) || [];
  const titleAcronyms = title.match(/\b[A-Z]{2,}\b/g) || [];
  const matchingAcronyms = queryAcronyms.filter(qa =>
    titleAcronyms.some(ta => ta === qa)
  );
  if (matchingAcronyms.length > 0) {
    score += matchingAcronyms.length * 5;
  }

  // ✅ BONUS/PÉNALITÉ PRÉFIXE : Valoriser le BON préfixe, pénaliser le MAUVAIS
  if (prefix) {
    const normalizedPrefix = normalizeStr(prefix);
    const normalizedTitleFull = normalizeStr(title);

    let titlePrefix = null;

    // 1️⃣ D'abord : Chercher dans la liste de préfixes connus (début/fin avec tiret, etc.)
    for (const p of ALL_PREFIXES_FOR_SCORING) {
      const patterns = [
        new RegExp(`-\\s*${p}\\s*$`, 'i'),       // - BTS à la fin
        new RegExp(`^${p}\\s*-`, 'i'),            // BTS - au début
        new RegExp(`^${p}\\b`, 'i')               // BTS au début
      ];

      if (patterns.some(pattern => pattern.test(normalizedTitleFull))) {
        titlePrefix = p;
        break;
      }
    }

    // 2️⃣ Ensuite : Chercher TOUT ce qui est entre parenthèses (prioritaire)
    const parenthesesMatch = title.match(/\(([^)]+)\)/);
    if (parenthesesMatch) {
      // Préfixe trouvé entre parenthèses → remplace le précédent
      titlePrefix = normalizeStr(parenthesesMatch[1]);
    }

    if (titlePrefix) {
      // Si un préfixe est détecté dans le titre
      if (titlePrefix === normalizedPrefix || titlePrefix.includes(normalizedPrefix) || normalizedPrefix.includes(titlePrefix)) {
        // ✅ BON préfixe (match exact ou contient)
        score += 30; // +30 points
      } else {
        // ❌ MAUVAIS préfixe (ex: cherche BTS, trouve MASTER ou MC NIVEAU IV)
        score -= 30; // -30 points de pénalité
        console.log(`  ❌ PÉNALITÉ PRÉFIXE -30: cherche "${prefix}", trouve "${titlePrefix}"`);
      }
    } else {
      // Pas de préfixe détecté dans le titre, pas de bonus/pénalité
    }
  }

  // ✅ BONUS/PÉNALITÉ OPTION (A, B, C, etc.)
  // Détecter "option A", "option B", etc. dans la requête ET le titre
  const optionRegex = /option\s+([A-Z])\b/i;
  const queryOptionMatch = query.match(optionRegex);
  const titleOptionMatch = title.match(optionRegex);

  if (queryOptionMatch && titleOptionMatch) {
    const queryOption = queryOptionMatch[1].toUpperCase();
    const titleOption = titleOptionMatch[1].toUpperCase();

    if (queryOption === titleOption) {
      // ✅ Même option
      score += 15;
      console.log(`  ✅ BONUS OPTION +15: même option "${queryOption}"`);
    } else {
      // ❌ Option différente
      score -= 25;
      console.log(`  ❌ PÉNALITÉ OPTION -25: cherche option "${queryOption}", trouve option "${titleOption}"`);
    }
  } else if (queryOptionMatch && !titleOptionMatch) {
    // Cherche une option spécifique mais le titre n'en a pas → pénalité légère
    score -= 10;
    console.log(`  ⚠️  PÉNALITÉ OPTION -10: cherche option "${queryOptionMatch[1]}", titre sans option`);
  }

  // Limiter d'abord le score à 100
  score = Math.min(100, score);

  // PUIS appliquer la pénalité pour les certifications inactives
  if (!isActive) {
    score -= 20;
  }

  return Math.max(0, score);
};

/**
 * Configuration pour la recherche multi-types
 */
export const SEARCH_CONFIG = {
  stopWords: ['de', 'en', 'le', 'la', 'les', 'du', 'des', 'et', 'ou', 'un', 'une'],
  genericWords: ['titre', 'professionnel', 'tp', 'bac', 'pro', 'bts', 'cap', 'master', 'licence', 'diplome', 'certificat', 'conduite', 'production', 'productions', 'gestion', 'technique', 'techniques', 'responsable', 'charge', 'chef', 'agent', 'arts', 'metiers', 'metier', 'service', 'services', 'specialisation', 'manager', 'unite', 'unites', 'technicien', 'superieur', 'conseiller']
};

/**
 * Mapping des variantes de préfixes de diplômes
 * Clé: forme longue (normalisée) → Valeur: forme courte
 */
export const PREFIX_VARIANTS = {
  'TITRE PROFESSIONNEL': 'TP',
  'DIPLOME D\'ETAT': 'DE',
  'DIPLOME D ETAT': 'DE',
  'CERTIFICAT DE SPECIALISATION': 'CS',
  'BAC PRO': 'BAC',
  'BREVET PROFESSIONNEL': 'BP',
  'BREVET TECHNIQUE DES METIERS': 'BTM'
};

/**
 * Obtenir la variante courte d'un préfixe
 * Ex: "Titre professionnel" → "TP"
 *
 * @param {string} prefix - Préfixe à convertir
 * @returns {string|null} Variante courte ou null si pas de variante
 */
export const getPrefixVariant = (prefix) => {
  if (!prefix) return null;

  const normalizedPrefix = prefix.toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
    .replace(/['']/g, ' ')
    .trim();

  return PREFIX_VARIANTS[normalizedPrefix] || null;
};

/**
 * Préparer une requête pour l'extraction de mots-clés
 * @param {string} query - Requête à nettoyer
 * @returns {string} Requête nettoyée
 */
export const cleanQueryForKeywords = (query) => {
  return query
    .replace(/-/g, ' ')                    // tirets → espaces
    .replace(/[,;:.!?()]/g, ' ')           // virgules, points, etc. → espaces
    .replace(/\s+/g, ' ')                  // espaces multiples → 1 espace
    .trim();
};

/**
 * Extraire les mots d'une requête avec métadonnées
 * @param {string} query - Requête nettoyée
 * @returns {Array} Liste d'objets { word, isGeneric, length, originalIndex }
 */
export const extractWordsWithMetadata = (query) => {
  const { stopWords, genericWords } = SEARCH_CONFIG;

  return query.split(' ')
    .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
    .map((w, index) => ({
      word: w,
      isGeneric: genericWords.includes(w.toLowerCase()),
      length: w.length,
      originalIndex: index // ✅ Conserver l'ordre original
    }));
};

/**
 * Calculer la distance de Levenshtein entre deux chaînes
 * (pour la recherche floue/fuzzy)
 *
 * @param {string} str1 - Première chaîne
 * @param {string} str2 - Seconde chaîne
 * @returns {number} Distance de Levenshtein
 */
export const levenshteinDistance = (str1, str2) => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // suppression
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
};
